import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select, Toggle } from '@/components/ui/Input';

/**
 * Pengaturan AI: penyedia, alamat, kunci, dan model.
 *
 * Dibiarkan bebas alih-alih dikunci ke satu penyedia, karena hampir semua
 * penyedia hari ini berbicara bentuk permintaan yang sama dengan OpenAI.
 * Dengan tiga isian ini, Anda bisa memakai OpenAI, Groq, OpenRouter, Together,
 * atau model yang dijalankan sendiri, tanpa menunggu kami menambahkannya satu
 * per satu.
 *
 * Kunci tidak pernah dikirim balik utuh dari server; yang tampil hanya empat
 * huruf terakhirnya sebagai bukti ia tersimpan.
 */
const CONTOH_PENYEDIA = [
  { label: 'Anthropic (Claude)', bentuk: 'anthropic', baseUrl: '', model: 'claude-opus-5' },
  {
    label: 'OpenAI (ChatGPT)',
    bentuk: 'openai',
    baseUrl: 'https://api.openai.com/v1',
    model: 'gpt-4o-mini',
  },
  {
    label: 'Groq',
    bentuk: 'openai',
    baseUrl: 'https://api.groq.com/openai/v1',
    model: 'llama-3.3-70b-versatile',
  },
  {
    label: 'OpenRouter',
    bentuk: 'openai',
    baseUrl: 'https://openrouter.ai/api/v1',
    model: 'openai/gpt-4o-mini',
  },
];

/**
 * Formulir akun AI.
 *
 * Dipakai di dua tempat: daftar akun di Pengaturan, dan tombol tambah akun di
 * halaman Buat Scraping. Sengaja satu komponen, bukan dua yang mirip - kalau
 * formulir di halaman Buat Scraping hanya meminta kunci, akun yang lahir dari
 * sana tidak punya nama maupun penyedia, dan pengguna baru menyadarinya saat
 * membuka Pengaturan dan menemukan akun bernama seadanya.
 */
export const FormAkunAi = ({ awal, onSimpan, menyimpan, onBatal }) => {
  const [f, setF] = useState({
    nama: awal?.nama ?? '',
    deskripsi: awal?.deskripsi ?? '',
    bentuk: awal?.bentuk ?? 'anthropic',
    baseUrl: awal?.baseUrl ?? '',
    model: awal?.model ?? '',
    apiKey: '',
    utama: awal?.utama ?? false,
    aktif: awal?.aktif ?? true,
  });

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const ubah = (k) => (e) => set(k)(e.target.value);

  const pakaiContoh = (c) =>
    setF((s) => ({ ...s, bentuk: c.bentuk, baseUrl: c.baseUrl, model: c.model }));

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Nama" hint="Yang Anda baca sendiri di daftar, bukan nama penyedianya.">
          {(p) => (
            <Input
              {...p}
              value={f.nama}
              onChange={ubah('nama')}
              placeholder="Claude untuk halaman rumit"
            />
          )}
        </Field>
        <Field label="Bentuk API" hint="Menentukan susunan permintaannya.">
          {(p) => (
            <Select {...p} value={f.bentuk} onChange={ubah('bentuk')}>
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI dan yang sejenis</option>
            </Select>
          )}
        </Field>
      </div>

      <Field label="Keterangan" hint="Kapan sebaiknya akun ini dipakai. Boleh dikosongkan.">
        {(p) => (
          <Input
            {...p}
            value={f.deskripsi}
            onChange={ubah('deskripsi')}
            placeholder="Model kuat, dipakai kalau deteksi biasa gagal"
          />
        )}
      </Field>

      <div>
        <p className="mb-2 text-xs font-semibold text-[var(--text-secondary)]">Isi cepat</p>
        <div className="flex flex-wrap gap-1.5">
          {CONTOH_PENYEDIA.map((c) => (
            <button
              key={c.label}
              type="button"
              onClick={() => pakaiContoh(c)}
              className="rounded-full bg-[var(--surface-sunken)] px-3 py-1 text-xs font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Alamat API" hint="Kosongkan untuk memakai alamat resmi penyedianya.">
          {(p) => (
            <Input
              {...p}
              value={f.baseUrl}
              onChange={ubah('baseUrl')}
              placeholder="https://api.openai.com/v1"
            />
          )}
        </Field>
        <Field label="Model" hint="Nama model persis seperti di dokumentasi penyedianya.">
          {(p) => <Input {...p} value={f.model} onChange={ubah('model')} placeholder="gpt-4o-mini" />}
        </Field>
      </div>

      <Field
        label="Kunci API"
        hint={awal?.id ? 'Kosongkan kalau tidak ingin menggantinya.' : undefined}
      >
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="new-password"
            value={f.apiKey}
            onChange={ubah('apiKey')}
            placeholder={awal?.kunciJejak ? `Tersimpan ${awal.kunciJejak}, biarkan kosong` : 'sk-...'}
          />
        )}
      </Field>

      <Toggle
        checked={f.utama}
        onChange={set('utama')}
        label="Jadikan akun utama"
        description="Dipakai kalau Anda tidak memilih apa pun saat menekan Deteksi. Hanya satu akun yang bisa menjadi utama."
      />
      <Toggle
        checked={f.aktif}
        onChange={set('aktif')}
        label="Aktif"
        description="Kalau dimatikan, akun ini tetap tersimpan tetapi tidak muncul sebagai pilihan."
      />

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onBatal}>
          Batal
        </Button>
        <Button
          loading={menyimpan}
          disabled={!f.nama.trim() || (!awal?.id && !f.apiKey.trim())}
          onClick={() => onSimpan(f)}
        >
          Simpan
        </Button>
      </div>
    </div>
  );
};
